import {containsAtLeastOneOf} from "@/shared/utils/string";

export interface LengthRule {
	required: boolean;
	minimumLength: number;
}

export interface CharacterRule {
	required: boolean;
	allowedCharacters: string;
}

export interface PasswordStrengthValidation {
	minimumLength: LengthRule;
	lowercase: CharacterRule;
	uppercase: CharacterRule;
	special: CharacterRule;
	digit: CharacterRule;
}

export interface PasswordStrengthValidationResult {
	isValid: boolean;
	errors?: string[];
}

export class PasswordStrengthValidationConfig {

	private static readonly LENGTH_RULE_NAMES: (keyof PasswordStrengthValidation)[] = ["minimumLength"];
	private static readonly CHARACTER_RULE_NAMES: (keyof PasswordStrengthValidation)[] = ["lowercase", "uppercase", "special", "digit"];

	private readonly ruleSet: PasswordStrengthValidation;

	constructor(ruleSet: PasswordStrengthValidation) {
		if (!ruleSet) {
			throw new Error("No ruleSet was passed");
		}

		this.ruleSet = ruleSet;
	}

	public validate(input: string): PasswordStrengthValidationResult {
		const errors: string[] = [];

		// check for minimum length
		PasswordStrengthValidationConfig.LENGTH_RULE_NAMES.forEach((lengthRuleName) => {
			const lengthRule = this.ruleSet[lengthRuleName] as LengthRule;
			if (lengthRule === undefined || lengthRule.required === false) {
				return;
			}

			if (input === undefined || input.length === 0 || input.length < lengthRule.minimumLength) {
				errors.push(lengthRuleName);
			}
		});

		// check for lowercase, uppercase, special, digit
		PasswordStrengthValidationConfig.CHARACTER_RULE_NAMES.forEach((characterRuleName) => {
			const characterRule = this.ruleSet[characterRuleName] as CharacterRule;
			if (characterRule === undefined || characterRule.required === false) {
				return;
			}

			if (input === undefined || input.length === 0 || !containsAtLeastOneOf(input, characterRule.allowedCharacters)) {
				errors.push(characterRuleName);
			}
		});

		return {
			isValid: errors.length === 0,
			errors,
		};
	}

	public static getRuleNames(): (keyof PasswordStrengthValidation)[] {
		return [
			...PasswordStrengthValidationConfig.LENGTH_RULE_NAMES,
			...PasswordStrengthValidationConfig.CHARACTER_RULE_NAMES,
		];
	}
}
